import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import type { FiatWalletAccountState } from '#src/__generated__/globalTypes.ts';
import {
  FiatWalletKycState,
  SCAEnrollmentState,
} from '#src/__generated__/globalTypes.ts';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { ReviewInfo } from '#src/components/kyc/KycFlow/ReviewInfo.tsx';
import { VerificationInProgress } from '#src/components/kyc/KycFlow/VerificationInProgress.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { useCashWalletKycEvents } from '#src/hooks/wallets/useCashWalletKycEvents.ts';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';

import { ActivationSuccess } from './ActivationSuccess/index.tsx';
import { DeclarativeForm } from './DeclarativeForm/index.tsx';
import { UploadAndSubmitProofOfIdentity as DocumentCheck } from './DocumentCheck/index.tsx';
import { HandleIdReviewError } from './HandleIdReviewError/index.tsx';
import { Intro } from './Intro/index.tsx';
import { MangopayScaStep } from './MangopayScaStep/index.tsx';
import { ActivateYourCashWallet } from './Onboarding/ActivateYourCashWallet/index.tsx';
import { WhatsNew } from './Onboarding/WhatsNew/index.tsx';
import { CreateFiatWalletSteps, OrderedSteps } from './type.ts';
import { useBackButtonTargets } from './useBackButtonTargets.ts';

const Content = styled(Vertical).attrs({ gap: 3 })`
  justify-content: flex-start;
`;
const Body = styled(Content)`
  padding: var(--double-unit);
  min-height: 100%;
`;

export type Props = {
  onClose: () => void;
  statusTarget: FiatWalletAccountState;
  canDismissAfterActivation: boolean;
  activationSuccessCta?: ReactNode;
  unsupportedCountryCta?: ReactNode;
  onDismissActivationSuccess?: () => void;
  onDeclarativeFormSuccess?: () => void;
  initialStep?: CreateFiatWalletSteps;
  onDismissBeforeActivation?: () => void;
};

export const CreateFiatWallet = ({
  initialStep: initialStepProp,
  onDismissActivationSuccess,
  onDeclarativeFormSuccess,
  onClose,
  activationSuccessCta,
  unsupportedCountryCta,
  statusTarget,
  canDismissAfterActivation,
  onDismissBeforeActivation,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { isFiatWalletOwner, kycStatus, canDepositAndWithdraw } =
    useFiatBalance();

  const initialStep = useMemo(() => {
    if (initialStepProp) return initialStepProp;
    if (
      kycStatus &&
      [
        FiatWalletKycState.OUT_OF_DATE,
        FiatWalletKycState.REFUSED,
      ].includesValue(kycStatus)
    )
      return CreateFiatWalletSteps.HANDLE_ID_REVIEW_ERROR;
    if (
      kycStatus &&
      [
        FiatWalletKycState.VALIDATION_ASKED,
        FiatWalletKycState.CREATED,
      ].includesValue(kycStatus)
    )
      return CreateFiatWalletSteps.DOCUMENT_UNDER_REVIEW;

    return CreateFiatWalletSteps.INTRO;
  }, [initialStepProp, kycStatus]);
  const { trackExit } = useCashWalletKycEvents();
  const [deepestStepReached, setDeepestStepReached] =
    useState<CreateFiatWalletSteps>(initialStep);

  const backTargets = useBackButtonTargets({ initialStep });
  const { up: isTablet } = useScreenSize('tablet');

  const [step, setStateStep] = useState<CreateFiatWalletSteps>(initialStep);

  const setStep = (s: CreateFiatWalletSteps) => {
    if (OrderedSteps[s] > OrderedSteps[deepestStepReached])
      setDeepestStepReached(s);
    setStateStep(s);
  };

  useEffect(() => {
    return () => {
      trackExit(deepestStepReached);
    };
  }, [deepestStepReached, trackExit]);

  const onModalClose = () => {
    if (
      initialStep === CreateFiatWalletSteps.INTRO &&
      step === CreateFiatWalletSteps.INTRO
    )
      return onClose;
    if (
      [
        CreateFiatWalletSteps.WHATS_NEW,
        CreateFiatWalletSteps.HANDLE_ID_REVIEW_ERROR,
        CreateFiatWalletSteps.DOCUMENT_UNDER_REVIEW,
      ].includes(step)
    )
      return onClose;
    if (
      CreateFiatWalletSteps.ACTIVATION_SUCCESS === step &&
      canDismissAfterActivation
    ) {
      return onClose;
    }
    return undefined;
  };

  const onBack = () => {
    if (step && backTargets[step])
      return () => {
        if (step && backTargets[step]) {
          setStep(backTargets[step]!);
        } else onClose();
      };
    return undefined;
  };
  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      onBack={onBack()}
      open
      onClose={onModalClose()}
      fullScreen={!isTablet}
    >
      <Body>
        {step === CreateFiatWalletSteps.WHATS_NEW && (
          <WhatsNew setStep={setStep} onDismiss={onDismissBeforeActivation} />
        )}
        {step === CreateFiatWalletSteps.ACTIVATE_YOUR_CASH_WALLET && (
          <ActivateYourCashWallet
            setStep={setStep}
            onDismiss={onDismissBeforeActivation}
          />
        )}
        {step === CreateFiatWalletSteps.HANDLE_ID_REVIEW_ERROR && (
          <HandleIdReviewError setStep={setStep} />
        )}
        {step === CreateFiatWalletSteps.INTRO && (
          <Intro
            canDismissAfterActivation={canDismissAfterActivation}
            statusTarget={statusTarget}
            onGetStarted={() => {
              if (!isFiatWalletOwner) {
                setStep(CreateFiatWalletSteps.TELL_US_ABOUT_YOU);
                return;
              }
              if (
                currentUser?.mangopayUser?.scaEnrollmentState ===
                SCAEnrollmentState.SCA_PENDING_CONFIRMATION
              ) {
                setStep(CreateFiatWalletSteps.MANGOPAY_SCA);
                return;
              }
              setStep(CreateFiatWalletSteps.REVIEW_INFO_BEFORE_ADDING_ID);
            }}
          />
        )}
        {[
          CreateFiatWalletSteps.TELL_US_ABOUT_YOU,
          CreateFiatWalletSteps.CHOOSE_CURRENCY,
          CreateFiatWalletSteps.REVIEW_INFORMATION,
        ].includes(step) && (
          <DeclarativeForm
            step={step}
            unsupportedCountryCta={unsupportedCountryCta}
            setStep={setStep}
            onClose={onClose}
            onSuccess={onDeclarativeFormSuccess}
          />
        )}
        {step === CreateFiatWalletSteps.ACTIVATION_SUCCESS && (
          <ActivationSuccess
            statusTarget={statusTarget}
            setStep={setStep}
            canDismissAfterActivation={canDismissAfterActivation}
            cta={activationSuccessCta}
            onDismiss={onDismissActivationSuccess}
          />
        )}
        {step === CreateFiatWalletSteps.MANGOPAY_SCA && (
          <MangopayScaStep
            setStep={setStep}
            onSuccess={() => {
              if (canDepositAndWithdraw) {
                // Case when user has been SCA downgraded but is still validated owner
                onDismissActivationSuccess?.();
                return;
              }
              setStep(CreateFiatWalletSteps.ACTIVATION_SUCCESS);
            }}
          />
        )}
        {step === CreateFiatWalletSteps.REVIEW_INFO_BEFORE_ADDING_ID && (
          <ReviewInfo
            onNext={() => setStep(CreateFiatWalletSteps.CHOOSE_DOCUMENT)}
          />
        )}
        {[
          CreateFiatWalletSteps.CHOOSE_DOCUMENT,
          CreateFiatWalletSteps.UPLOAD,
        ].includes(step) && (
          <DocumentCheck setStep={setStep} currentStep={step} />
        )}
        {[CreateFiatWalletSteps.DOCUMENT_UNDER_REVIEW].includes(step) && (
          <VerificationInProgress onDone={onClose} />
        )}
      </Body>
    </Dialog>
  );
};
